import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { GazetteIssue, GazetteIssuePublished, GazetteNotice, GazetteNoticeCategory, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const NOTICE_CATEGORIES: GazetteNoticeCategory[] = [
  'ACT_COMMENCEMENT',
  'LEGAL_NOTICE',
  'GENERAL_NOTICE',
  'APPOINTMENT',
  'STATUTORY_INSTRUMENT',
  'CORRIGENDUM',
];

export function GazetteIssueDetail() {
  const { id } = useParams<{ id: string }>();
  const { persona } = useAuth();
  const isEditor = persona?.roles.includes('GAZETTE_EDITOR') ?? false;

  const issueQ = useApiQuery<GazetteIssue>(id ? `/api/v1/m38/gazette-issues/${id}` : null);
  const noticesQ = useApiQuery<Page<GazetteNotice>>(id ? `/api/v1/m38/gazette-issues/${id}/notices?limit=100` : null);

  const [showAddNotice, setShowAddNotice] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishResult, setPublishResult] = useState<GazetteIssuePublished | null>(null);

  const publish = async () => {
    if (!id) return;
    setPublishBusy(true);
    setPublishError(null);
    try {
      const res = await api.post<GazetteIssuePublished>(`/api/v1/m38/gazette-issues/${id}/publish`);
      setPublishResult(res);
      issueQ.reload();
    } catch (e) {
      setPublishError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Publish failed');
    } finally {
      setPublishBusy(false);
    }
  };

  if (issueQ.loading) return <Spinner label="Loading issue…" />;
  if (issueQ.error) return <ErrorNote message={issueQ.error} />;
  const issue = issueQ.data;
  if (!issue) return null;

  return (
    <div>
      <PageHeader
        title={`Gazette No. ${issue.issueNumber} of ${issue.issueYear}`}
        subtitle={formatDate(issue.issueDate)}
        actions={<StatusBadge status={issue.status} />}
      />

      {publishError ? (
        <div className="mb-4">
          <ErrorNote message={publishError} />
        </div>
      ) : null}
      {publishResult ? (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800">
          Published — {publishResult.subscribersNotified} subscriber(s) notified
          {publishResult.subscribersFailed ? `, ${publishResult.subscribersFailed} failed` : ''}.
        </div>
      ) : null}

      {issue.permanentUrl || issue.docVaultSerial ? (
        <div className="card mb-5 p-4 text-xs text-slate-500">
          {issue.permanentUrl ? (
            <div>
              Permanent URL: <code>{issue.permanentUrl}</code>
            </div>
          ) : null}
          {issue.docVaultSerial ? (
            <div className="mt-1">
              DocVault serial <code>{issue.docVaultSerial}</code>
              {issue.docVaultVerifyUrl ? (
                <>
                  {' '}
                  ·{' '}
                  <a href={issue.docVaultVerifyUrl} target="_blank" rel="noreferrer" className="text-brand-700 underline">
                    verify ↗
                  </a>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {isEditor && issue.status === 'COMPILING' ? (
        <div className="mb-5 flex flex-wrap gap-2">
          <button className="btn-primary" onClick={() => setShowAddNotice(true)}>
            + Add notice
          </button>
          <button className="btn-success" onClick={publish} disabled={publishBusy}>
            {publishBusy ? 'Publishing…' : 'Publish issue'}
          </button>
        </div>
      ) : null}

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Notices</h2>
        {noticesQ.loading ? (
          <Spinner />
        ) : noticesQ.error ? (
          <ErrorNote message={noticesQ.error} />
        ) : (noticesQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No notices in this issue yet" />
        ) : (
          <ul className="space-y-2.5">
            {noticesQ.data!.items.map((n) => (
              <li key={n.id} className="rounded-lg border border-slate-200 p-3.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-900">
                    {n.noticeNumber}. {n.title}
                  </span>
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{n.category.replaceAll('_', ' ')}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{n.bodyText}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAddNotice && id ? (
        <AddNoticeModal
          issueId={id}
          onClose={() => setShowAddNotice(false)}
          onDone={() => {
            setShowAddNotice(false);
            noticesQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function AddNoticeModal({ issueId, onClose, onDone }: { issueId: string; onClose: () => void; onDone: () => void }) {
  const [category, setCategory] = useState<GazetteNoticeCategory>('GENERAL_NOTICE');
  const [title, setTitle] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [relatedLegislationId, setRelatedLegislationId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m38/gazette-issues/${issueId}/notices`, {
        category,
        title,
        bodyText,
        relatedLegislationId: relatedLegislationId || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to add notice');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add notice" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Category">
          <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as GazetteNoticeCategory)}>
            {NOTICE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Body text">
          <textarea className="field-input" rows={5} value={bodyText} onChange={(e) => setBodyText(e.target.value)} />
        </Field>
        <Field label="Related legislation id (optional)">
          <input className="field-input" value={relatedLegislationId} onChange={(e) => setRelatedLegislationId(e.target.value)} placeholder="legislation id, if this notice relates to one" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || title.trim().length < 3 || !bodyText.trim()}>
            {busy ? 'Adding…' : 'Add notice'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
